import { onScopeDispose, ref, toValue, watch } from 'vue'

// Invalidate as soon as the input changes, including the debounce interval.
// A slow response must never become an option for a different query.
export function useTypeaheadSearch(query, {
  search,
  enabled = true,
  context = '',
  delay = 250,
  minimumLength = 2,
}) {
  const items = ref([])
  const loading = ref(false)
  const error = ref(false)
  const hasSearched = ref(false)
  let timer = null
  let sequence = 0

  function reset() {
    if (timer !== null) clearTimeout(timer)
    timer = null
    sequence++
    items.value = []
    loading.value = false
    error.value = false
    hasSearched.value = false
  }

  function refresh() {
    reset()
    const term = String(toValue(query) || '').trim()
    if (!toValue(enabled) || term.length < minimumLength) return
    const request = sequence
    loading.value = true
    timer = setTimeout(async () => {
      timer = null
      try {
        const result = await search(term)
        if (request !== sequence) return
        items.value = Array.isArray(result) ? result : []
      } catch {
        if (request !== sequence) return
        error.value = true
      } finally {
        if (request === sequence) {
          loading.value = false
          hasSearched.value = true
        }
      }
    }, delay)
  }

  watch([() => toValue(query), () => toValue(enabled), () => toValue(context)], refresh, {
    immediate: true,
    flush: 'sync',
  })
  onScopeDispose(reset)

  return { items, loading, error, hasSearched, refresh, reset }
}
